/**
 * The limits a trader may set, what kind of value each takes, and what each is
 * when nobody has set it.
 *
 * **One copy.** The policy route accepts exactly these fields, `@atc/oms`
 * builds `DEFAULT_TRADING_POLICY` from these defaults, and the terminal's
 * `policy set` parses, completes and documents from the same list. Until
 * `M7/I3` the terminal kept a hand copy of the route's field list, and the
 * defaults were pasted into one of its error sentences.
 *
 * The defaults are in wire form — decimal strings for money — because that is
 * the form every reader can use: `oms` parses them with `money()`, and the
 * terminal prints them. They restate the `trading_policies` column defaults,
 * which `apps/api/test/policy-defaults.test.ts` asserts.
 */

export const POLICY_LIMITS = [
  { name: "kill_switch", kind: "switch" },
  { name: "max_notional_per_order", kind: "money" },
  { name: "max_intraday_notional_per_order", kind: "money" },
  { name: "max_position_notional", kind: "money" },
  { name: "max_orders_per_day", kind: "count" },
  { name: "symbol_allowlist", kind: "symbols" },
  { name: "symbol_denylist", kind: "symbols" },
  { name: "agents_may_trade", kind: "switch" },
] as const;

export type PolicyLimitName = (typeof POLICY_LIMITS)[number]["name"];
export type PolicyLimitKind = (typeof POLICY_LIMITS)[number]["kind"];

export const POLICY_LIMIT_NAMES: readonly PolicyLimitName[] = POLICY_LIMITS.map((l) => l.name);

export const isPolicyLimitName = (name: string): name is PolicyLimitName =>
  (POLICY_LIMIT_NAMES as readonly string[]).includes(name);

/** `undefined` for a name that is not a limit — never a guess. */
export const policyLimitKind = (name: string): PolicyLimitKind | undefined =>
  POLICY_LIMITS.find((l) => l.name === name)?.kind;

export const POLICY_DEFAULTS = {
  kill_switch: false,
  max_notional_per_order: "25000.0000",
  max_intraday_notional_per_order: "100.0000",
  max_position_notional: "100000.0000",
  max_orders_per_day: 500,
  symbol_allowlist: [] as readonly string[],
  symbol_denylist: [] as readonly string[],
  agents_may_trade: true,
} as const satisfies Record<PolicyLimitName, unknown>;
